/** Mouse-driven bbox drawer for MapLibre GL. Not a React component — it's a
 * small controller that attaches handlers to a Map instance and draws a
 * rectangle as the user click-drags.
 *
 * The bbox is clamped to `maxDiagonalMi` during dragging — the user can pull
 * the cursor wherever they like but the rectangle stops growing past the cap,
 * so we never throw a "bbox too large" error at commit time.
 */

import type { Map as MLMap, GeoJSONSource } from "maplibre-gl";

import type { BBox } from "../lib/api";

const SRC = "__bbox_draw_src__";
const LAYER_FILL = "__bbox_draw_fill__";
const LAYER_LINE = "__bbox_draw_line__";

function haversineMi(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.7613;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export interface BboxDrawerOptions {
  maxDiagonalMi: number;
}

export class BboxDrawer {
  private map: MLMap;
  private dragging = false;
  private startLngLat: [number, number] | null = null;
  private currentLngLat: [number, number] | null = null;
  private onCommit: (bbox: BBox) => void;
  private onCancel: () => void;
  private originalCursor = "";
  private maxDiagonalMi: number;

  constructor(
    map: MLMap,
    onCommit: (b: BBox) => void,
    onCancel: () => void,
    options: BboxDrawerOptions = { maxDiagonalMi: 500 },
  ) {
    this.map = map;
    this.onCommit = onCommit;
    this.onCancel = onCancel;
    this.maxDiagonalMi = options.maxDiagonalMi;
  }

  begin(): void {
    this.originalCursor = this.map.getCanvas().style.cursor;
    this.map.getCanvas().style.cursor = "crosshair";
    this.map.dragPan.disable();
    this.map.on("mousedown", this.handleDown);
    this.map.on("mousemove", this.handleMove);
    this.map.on("mouseup", this.handleUp);
    document.addEventListener("keydown", this.handleKey);
    this.ensureLayers();
  }

  end(): void {
    this.map.getCanvas().style.cursor = this.originalCursor;
    this.map.dragPan.enable();
    this.map.off("mousedown", this.handleDown);
    this.map.off("mousemove", this.handleMove);
    this.map.off("mouseup", this.handleUp);
    document.removeEventListener("keydown", this.handleKey);
    this.clear();
  }

  clear(): void {
    this.dragging = false;
    this.startLngLat = null;
    this.currentLngLat = null;
    const src = this.map.getSource(SRC) as GeoJSONSource | undefined;
    if (src) src.setData({ type: "FeatureCollection", features: [] });
  }

  private ensureLayers(): void {
    if (!this.map.getSource(SRC)) {
      this.map.addSource(SRC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      this.map.addLayer({
        id: LAYER_FILL, type: "fill", source: SRC,
        paint: { "fill-color": "#3a7bd5", "fill-opacity": 0.15 },
      });
      this.map.addLayer({
        id: LAYER_LINE, type: "line", source: SRC,
        paint: { "line-color": "#3a7bd5", "line-width": 2 },
      });
    }
  }

  private handleDown = (e: any) => {
    this.dragging = true;
    this.startLngLat = [e.lngLat.lng, e.lngLat.lat];
    this.currentLngLat = this.startLngLat;
    this.redraw();
  };

  private handleMove = (e: any) => {
    if (!this.dragging || !this.startLngLat) return;
    this.currentLngLat = this.clampToMaxDiagonal(
      this.startLngLat,
      [e.lngLat.lng, e.lngLat.lat],
    );
    this.redraw();
  };

  /** If the diagonal exceeds maxDiagonalMi, shrink toward start along the drag vector. */
  private clampToMaxDiagonal(
    start: [number, number],
    target: [number, number],
  ): [number, number] {
    const [lon0, lat0] = start;
    const [lon1, lat1] = target;
    const diag = haversineMi(lat0, lon0, lat1, lon1);
    if (diag <= this.maxDiagonalMi) return target;
    // Scale along the lon/lat vector to cap the diagonal
    const scale = this.maxDiagonalMi / diag;
    // The haversine scaling isn't linear in lon/lat when lat spans large ranges,
    // but for a 75-mi cap the linearization is accurate to <0.5 mi.
    return [
      lon0 + (lon1 - lon0) * scale,
      lat0 + (lat1 - lat0) * scale,
    ];
  }

  private handleUp = () => {
    if (!this.dragging || !this.startLngLat || !this.currentLngLat) {
      this.dragging = false;
      return;
    }
    const bbox = this.normalize(this.startLngLat, this.currentLngLat);
    this.dragging = false;
    this.redraw();
    this.onCommit(bbox);
  };

  private handleKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.onCancel();
    }
  };

  private normalize(a: [number, number], b: [number, number]): BBox {
    return {
      lon_min: Math.min(a[0], b[0]),
      lat_min: Math.min(a[1], b[1]),
      lon_max: Math.max(a[0], b[0]),
      lat_max: Math.max(a[1], b[1]),
    };
  }

  private redraw(): void {
    if (!this.startLngLat || !this.currentLngLat) return;
    const bb = this.normalize(this.startLngLat, this.currentLngLat);
    const diagMi = haversineMi(bb.lat_min, bb.lon_min, bb.lat_max, bb.lon_max);
    const atCap = diagMi > this.maxDiagonalMi - 0.5;
    const color = atCap ? "#d8803b" : "#3a7bd5";
    const src = this.map.getSource(SRC) as GeoJSONSource;
    src.setData({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [[
              [bb.lon_min, bb.lat_min],
              [bb.lon_max, bb.lat_min],
              [bb.lon_max, bb.lat_max],
              [bb.lon_min, bb.lat_max],
              [bb.lon_min, bb.lat_min],
            ]],
          },
          properties: { diagMi, atCap, color },
        },
      ],
    });
    // Update the paint to reflect the cap
    try {
      this.map.setPaintProperty(LAYER_FILL, "fill-color", color);
      this.map.setPaintProperty(LAYER_LINE, "line-color", color);
    } catch { /* layer may not exist yet on first draw */ }
  }
}
